import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Compass, Lock, Eye, EyeOff, CheckCircle, XCircle, ArrowRight } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';

export const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { language } = useLanguage();
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);

  const content = {
    en: {
      title: 'Create New Password',
      subtitle: 'Enter your new password below',
      password: 'New Password',
      confirmPassword: 'Confirm Password',
      resetButton: 'Reset Password',
      resetting: 'Resetting...',
      success: 'Password Reset!',
      successMessage: 'Your password has been successfully reset. You can now log in with your new password.',
      goToLogin: 'Go to Login',
      invalidToken: 'Invalid Reset Link',
      invalidMessage: 'This reset link is invalid or has expired. Please request a new password reset.',
      requestNew: 'Request New Reset',
      verifying: 'Verifying...',
      passwordMismatch: 'Passwords do not match',
      passwordTooShort: 'Password must be at least 6 characters',
    },
    tr: {
      title: 'Yeni Şifre Oluştur',
      subtitle: 'Yeni şifreni aşağıya gir',
      password: 'Yeni Şifre',
      confirmPassword: 'Şifreyi Onayla',
      resetButton: 'Şifreyi Sıfırla',
      resetting: 'Sıfırlanıyor...',
      success: 'Şifre Sıfırlandı!',
      successMessage: 'Şifren başarıyla sıfırlandı. Artık yeni şifrenle giriş yapabilirsin.',
      goToLogin: 'Girişe Git',
      invalidToken: 'Geçersiz Sıfırlama Bağlantısı',
      invalidMessage: 'Bu sıfırlama bağlantısı geçersiz veya süresi dolmuş. Lütfen yeni bir şifre sıfırlama iste.',
      requestNew: 'Yeni Sıfırlama İste',
      verifying: 'Doğrulanıyor...',
      passwordMismatch: 'Şifreler eşleşmiyor',
      passwordTooShort: 'Şifre en az 6 karakter olmalı',
    },
  };

  const c = content[language] || content.en;

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setVerifying(false);
        setTokenValid(false);
        return;
      }

      try {
        await api.get(`/auth/verify-reset-token?token=${token}`);
        setTokenValid(true);
      } catch (error) {
        setTokenValid(false);
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password.length < 6) {
      toast.error(c.passwordTooShort);
      return;
    }

    if (password !== confirmPassword) {
      toast.error(c.passwordMismatch);
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/reset-password', {
        token,
        new_password: password,
      });
      setSuccess(true);
      toast.success(c.success);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  // Loading state while verifying token
  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <div className="w-16 h-16 rounded-full bg-[#D4AF37]/20 flex items-center justify-center mx-auto mb-4 border border-[#D4AF37]/30 animate-pulse">
            <Compass className="w-8 h-8 text-[#D4AF37]" />
          </div>
          <p className="text-[#A1A1AA]">{c.verifying}</p>
        </motion.div>
      </div>
    );
  }

  // Invalid token state
  if (!tokenValid && !success) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="glass-panel rounded-2xl p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4 border border-red-500/30">
              <XCircle className="w-8 h-8 text-red-400" />
            </div>
            <h1 className="font-serif font-light text-2xl text-[#F8F5F2] mb-2">
              {c.invalidToken}
            </h1>
            <p className="text-[#A1A1AA] text-sm mb-6">
              {c.invalidMessage}
            </p>
            <button
              onClick={() => navigate('/auth')}
              className="w-full flex items-center justify-center gap-2 rounded-full border border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37] px-8 py-3.5 hover:bg-[#D4AF37] hover:text-[#030305] transition-all duration-300 font-sans font-medium"
              data-testid="request-new-reset"
            >
              {c.requestNew}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="glass-panel rounded-2xl p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4 border border-green-500/30">
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
            <h1 className="font-serif font-light text-2xl text-[#F8F5F2] mb-2">
              {c.success}
            </h1>
            <p className="text-[#A1A1AA] text-sm mb-6">
              {c.successMessage}
            </p>
            <button
              onClick={() => navigate('/auth')}
              className="w-full flex items-center justify-center gap-2 rounded-full border border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37] px-8 py-3.5 hover:bg-[#D4AF37] hover:text-[#030305] transition-all duration-300 font-sans font-medium"
              data-testid="go-to-login"
            >
              {c.goToLogin}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  // Reset password form
  return (
    <div className="min-h-screen relative flex flex-col items-center justify-center px-6 py-12">
      <div className="absolute inset-0 bg-gradient-to-b from-[#D4AF37]/5 via-transparent to-transparent pointer-events-none" />
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md relative z-10"
      >
        {/* Header */}
        <div className="text-center mb-10">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="w-20 h-20 rounded-full bg-[#D4AF37]/20 flex items-center justify-center mx-auto mb-6 border border-[#D4AF37]/30"
          >
            <Lock className="w-10 h-10 text-[#D4AF37]" />
          </motion.div>
          
          <h1 className="font-serif font-light text-3xl text-[#F8F5F2] mb-2">
            {c.title}
          </h1>
          <p className="text-[#A1A1AA] text-sm">
            {c.subtitle}
          </p>
        </div>

        {/* Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass-panel rounded-2xl p-8"
        >
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="reset-password-form">
            <div>
              <label className="font-sans uppercase tracking-[0.2em] text-[#D4AF37] text-xs font-semibold block mb-2">
                {c.password}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-black/40 border border-[#D4AF37]/20 rounded-xl px-4 py-3 pr-12 text-[#F8F5F2] font-sans focus:ring-2 focus:ring-[#D4AF37]/50 focus:border-[#D4AF37]/50 focus:outline-none transition-all"
                  required
                  minLength={6}
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7280] hover:text-[#D4AF37] transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="font-sans uppercase tracking-[0.2em] text-[#D4AF37] text-xs font-semibold block mb-2">
                {c.confirmPassword}
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full bg-black/40 border border-[#D4AF37]/20 rounded-xl px-4 py-3 pr-12 text-[#F8F5F2] font-sans focus:ring-2 focus:ring-[#D4AF37]/50 focus:border-[#D4AF37]/50 focus:outline-none transition-all"
                  required
                  minLength={6}
                  data-testid="confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7280] hover:text-[#D4AF37] transition-colors"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 rounded-full border border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37] px-8 py-3.5 hover:bg-[#D4AF37] hover:text-[#030305] transition-all duration-300 shadow-[0_0_20px_rgba(212,175,55,0.15)] hover:shadow-[0_0_30px_rgba(212,175,55,0.4)] font-sans font-medium disabled:opacity-50"
              data-testid="reset-password-submit"
            >
              {loading ? c.resetting : c.resetButton}
              {!loading && <ArrowRight className="w-4 h-4" />}
            </button>
          </form>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default ResetPassword;
